import { format, parseISO } from "date-fns";
import { useFormContext } from "react-hook-form";
import { Box, Grid, Typography } from "@mui/material";

import { FormDatePicker, FormTextField } from "@covertree/ui";
import { useEndorsementOutletContext } from "@layouts/EndorsementLayout";
import {
  InsuredTabFormValues,
  selectEndorsementEffectiveDateBounds,
  useEndorsementSelector,
} from "@redux/slices/endorsement";

interface PolicyTermProps {
  viewOnly?: boolean;
}

const formatTermDate = (date: string) => format(parseISO(date), "MMM d, yyyy");

export const PolicyTerm = ({ viewOnly = false }: PolicyTermProps) => {
  const { policy: { startDate, endDate } = {} } = useEndorsementOutletContext();

  const { minDate, maxDate } = useEndorsementSelector(
    selectEndorsementEffectiveDateBounds
  );

  const { control } = useFormContext<InsuredTabFormValues>();

  const termRange =
    startDate && endDate
      ? `${formatTermDate(startDate)} → ${formatTermDate(endDate)}`
      : undefined;

  return (
    <Box
      position="relative"
      border="1px solid"
      borderRadius="20px"
      p={3}
      my={1}
      width={1}
      fontSize={16}
      borderColor="action.disabled">
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        pb={3}
        borderBottom="1px solid"
        borderColor="action.disabled">
        <Grid item>
          <Typography
            fontSize="inherit"
            variant="body2"
            color="text.disabled"
            mb={1}>
            Policy Term
          </Typography>

          {termRange && (
            <Typography
              fontSize="inherit"
              variant="body2"
              sx={{ fontSize: 18 }}>
              {termRange}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        gap={1.5}
        fontSize="inherit">
        <FormDatePicker
          id="endorsement-effective-date"
          name="endorsementEffectiveDate"
          label="Endorsement effective date"
          control={control}
          minDate={minDate}
          maxDate={maxDate}
          readOnly
          fontSize={18}
          labelFontSize={16}
          disabled={viewOnly || !minDate || !maxDate}
          dataTestId="endorsement-effective-date"
        />

        <FormTextField
          id="reason"
          name="reason"
          fontSize={18}
          labelFontSize={16}
          label="Reason / memo"
          control={control}
          dataTestId="reason"
          disabled={viewOnly}
        />
      </Box>
    </Box>
  );
};
